import debug from 'debug';
import React from 'react';
import { Constants } from '../../../../Constants';
import { IAppConfigResult, useAppConfig } from '../../../../libs/hooks/useAppConfig';
import { GetResponseOptions, useChat } from '../../../../libs/hooks/useChat';
import { AlertType } from '../../../../libs/models/AlertType';
import { ChatMessageType } from '../../../../libs/models/ChatMessage';
import { IAskResult } from '../../../../libs/semantic-kernel/model/AskResult';
import { IAppConfigValue } from '../../../../libs/services/AppConfigService';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { addAlert } from '../../../../redux/features/app/appSlice';
import { editConversationBotProfilePicture, editConversationScope } from '../../../../redux/features/conversations/conversationsSlice';
import { getScopeIcon } from '../../../utils/IconUtils';
//import { AuroraText } from './Effects/AuroraText';

interface IChatGuidance {
    scope: string;
    header: string;
    text: string;
}

interface IChatGuidanceProps {
    onSubmit: (options: GetResponseOptions) => Promise<IAskResult | null>;
}

const log = debug(Constants.debug.root).extend('chat-guidance');

export const ChatRoomPrompts: React.FC<IChatGuidanceProps> = ({ onSubmit }) => {
    const [guidances, setGuidances] = React.useState<IChatGuidance[]>([]);

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chat = useChat();
    const dispatch = useAppDispatch();

    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const user = `${emailAddress}`;

    const appConfig = useAppConfig();

    const getSplashGuidanceContent = async () => {
        const response: IAppConfigResult<IAppConfigValue[]> = await appConfig.getConfigMultipleValues(
            'Splash Guidance',
            user,
        );
        if (response.Success) {
            const values = response.Data;
            const updatedGuidances: IChatGuidance[] = [];

            values?.forEach((value) => {
                const scope = value.Category ?? '';
                const header = value.CategoryTitle ?? '';
                const text = value.Value;
                updatedGuidances.push({ scope, header, text });
            });

            setGuidances(updatedGuidances);
        }
    };

    const handleSubmit = async (
        value: string,
        messageType: ChatMessageType = ChatMessageType.Message,
        scope = 'None',
    ) => {
        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        // if scope is DocumentUpload the set the scope to None
        if (scope === 'documentupload') {
            scope = 'None';
        }

        const chatState = conversations[selectedId];

        // if scope is different from the current scope, update the chat scope
        if (scope !== chatState.chatScope) {
            if (chatState.isSaved) {
                await chat.editChat(selectedId, chatState.title, chatState.memoryBalance, scope);
            }
            localStorage.setItem('lastSelectedChatScope', scope.toString());
            dispatch(editConversationScope({ id: selectedId, newScope: scope }));
            dispatch(
                editConversationBotProfilePicture({
                    id: selectedId,
                    newProfilePicture: getScopeIcon(scope),
                }),
            );
        }

        //dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Advania GPT is typing' }));
        onSubmit({ value, messageType, chatId: selectedId, scope }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            log(message);
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    React.useEffect(() => {
        getSplashGuidanceContent().catch((e) => {
            log('error getting splash guidance', e);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (guidances.length === 0) {
        return null;
    }

    return (
        <>
            <div className="items-center text-center">
                <p>Ask me a question or select from one of the samples below</p>
            </div>
            <ul
                role="list"
                className="flex flex-wrap justify-center gap-4 sm:gap-6 p-4 sm:p-6 text-slate-900 dark:text-slate-200">
                {guidances.map((guidance, index) => (
                    <li
                        key={index}
                        className={`flex-none w-full sm:w-1/2 lg:w-1/4 ${guidances.length % 3 !== 0 && index >= guidances.length - (guidances.length % 3)
                                ? 'lg:justify-self-center'
                                : ''
                            } rounded-lg bg-slate-100 shadow transition-colors duration-300 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600`}>
                        <button
                            className="flex flex-col w-full"
                            onClick={() => {
                                handleSubmit(guidance.text, ChatMessageType.Message, guidance.scope).catch((error) => {
                                    const message = `Error submitting chat input: ${(error as Error).message}`;
                                    log(message);
                                    dispatch(
                                        addAlert({
                                            type: AlertType.Error,
                                            message,
                                        }),
                                    );
                                });
                            }}>
                            <div className="flex w-full cursor-pointer items-center justify-between space-x-4 truncate p-3 md:p-6">
                                <div className="flex flex-col items-center gap-y-1 rounded-lg text-xs">
                                    <div className="w-8 h-8">
                                        <img
                                            src={getScopeIcon(guidance.scope)}
                                            alt="Bot Avatar"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 truncate text-left">
                                    <div className="flex items-center space-x-3">
                                        <h3 className="text-sm font-medium text-slate-900 transition-colors duration-300 group-hover:text-slate-800 dark:text-slate-200 dark:group-hover:text-slate-50">
                                            {guidance.header}
                                        </h3>
                                    </div>
                                    <p className="mt-1 truncate text-sm text-slate-500 transition-colors duration-300 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-300">
                                        {guidance.text}
                                    </p>
                                </div>
                            </div>
                        </button>
                    </li>
                ))}
            </ul>
        </>
    );
};
